import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/student-pre-interviews/StudentPreInterviewsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { StudentPreInterviewsFilter } from "./student-pre-interviews-filter/StudentPreInterviewsFilter";
import { StudentPreInterviewsTable } from "./student-pre-interviews-table/StudentPreInterviewsTable";
import { useStudentPreInterviewsUIContext } from "./StudentPreInterviewsUIContext";

export function StudentPreInterviewsCard() {
  // Student PreInterviews UI Context
  const preInterviewsUIContext = useStudentPreInterviewsUIContext();
  const preInterviewsUIProps = useMemo(() => {
    return {
      ids: preInterviewsUIContext.ids,
      setIds: preInterviewsUIContext.setIds,
      queryParams: preInterviewsUIContext.queryParams,
      openNewStudentPreInterviewPage: preInterviewsUIContext.openNewStudentPreInterviewPage,
    };
  }, [preInterviewsUIContext]);
    
  // Getting curret state of Student PreInterview list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentPreInterviews }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Student PreInterviews Redux state
  const dispatch = useDispatch();

  /// Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Ön Kayıt ve Görüşme Listesini Excele Aktarır */
  const exportStudentPreInterviewsToExcel = () => {
    dispatch(actions.getAllStudentPreInterviews(preInterviewsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 25, 25, 20, 30, 25, 50, 30, 15, 30, 25, 20];
      const columns = ["Personel", "Görüşme Tarihi",  "Görüşme Türü", "Veli Yakınlığı", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama", "Öğrenci Adı Soyadı", "Sınıfı", "Okulu", "Takip Tarihi", "Durum"];
      
      entities.forEach(entity => rows.push([
        entity.personnel,
        entity.date,
        entity.interviewTypeName,
        entity.relationshipName,
        entity.parentName,
        entity.parentPhone,
        entity.explanation,
        entity.studentName,        
        entity.studentGrade,
        entity.studentSchool,
        entity.trackingDate,
        entity.resultName
      ]));
      
      const filename = preInterviewsUIProps.queryParams.term + " - " + (preInterviewsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ön Kayıt ve Görüşme Listesi";
      const sheetname = "Ön Kayıt ve Görüşme Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Ön Kayıt ve Görüşme Listesini PDF e Aktarır */
  const exportStudentPreInterviewsToPDF = () => {
    dispatch(actions.getAllStudentPreInterviews(preInterviewsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Personel",
          rows: entities.map(entity => entity.personnel)
        },
        {
          header: "Görüşme Tarihi",
          rows: entities.map(entity => entity.date)
        },
        {
          header: "Görüşme Türü",
          rows: entities.map(entity => entity.interviewTypeName)
        },
        {
          header: "Veli Yakınlığı",
          rows: entities.map(entity => entity.relationshipName)
        },
        {
          header: "Veli Adı Soyadı",
          rows: entities.map(entity => entity.parentName)
        },
        {
          header: "Veli Telefonu",
          rows: entities.map(entity => entity.parentPhone)
        },
        {
          header: "Açıklama",
          rows: entities.map(entity => entity.explanation)
        },
        {
          header: "Öğrenci Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Sınıfı",
          rows: entities.map(entity => entity.studentGrade)
        },
        {
          header: "Okulu",
          rows: entities.map(entity => entity.studentSchool)
        },
        {
          header: "Takip Tarihi",
          rows: entities.map(entity => entity.trackingDate)
        },
        {
          header: "Durum",
          rows: entities.map(entity => entity.resultName)
        }
      ];
  
      const filename = preInterviewsUIProps.queryParams.term + " - " + (preInterviewsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ön Kayıt ve Görüşme Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Ön Kayıt ve Görüşme Listesini Yazdırma Penceresini Açar */
  const printStudentPreInterviews = () => {
    dispatch(actions.getAllStudentPreInterviews(preInterviewsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = preInterviewsUIProps.queryParams.term + " - " + (preInterviewsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ön Kayıt ve Görüşme Listesi";
      const properties = ["Personel", "Görüşme Tarihi",  "Görüşme Türü", "Veli Yakınlığı", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama", "Öğrenci Adı Soyadı", "Sınıfı", "Okulu", "Takip Tarihi", "Durum"];
  
      entities.forEach(entity => {
        data.push({
          "Personel": entity.personnel,
          "Görüşme Tarihi": entity.date,          
          "Görüşme Türü": entity.interviewTypeName ?? "",
          "Veli Yakınlığı": entity.relationshipName,
          "Veli Adı Soyadı": entity.parentName,
          "Veli Telefonu": entity.parentPhone,
          "Açıklama": entity.explanation,          
          "Öğrenci Adı Soyadı": entity.studentName,
          "Sınıfı": entity.studentGrade,
          "Okulu": entity.studentSchool,
          "Takip Tarihi": entity.trackingDate,
          "Durum": entity.resultName ?? ""
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Ön Kayıt ve Görüşme Listesini Panoya Kopyalar */
  const copyStudentPreInterviews = () => {
    dispatch(actions.getAllStudentPreInterviews(preInterviewsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Personel", "Görüşme Tarihi",  "Görüşme Türü", "Veli Yakınlığı", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama", "Öğrenci Adı Soyadı", "Sınıfı", "Okulu", "Takip Tarihi", "Durum"];
      const header = preInterviewsUIProps.queryParams.term + " - " + (preInterviewsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ön Kayıt ve Görüşme Listesi";
      
      entities.forEach(entity => rows.push([
        entity.personnel,
        entity.date,
        entity.interviewTypeName ?? "",
        entity.relationshipName,
        entity.parentName,
        entity.parentPhone,
        entity.explanation,
        entity.studentName,        
        entity.studentGrade,
        entity.studentSchool,
        entity.trackingDate,
        entity.resultName ?? ""
      ]));

      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Ön Kayıt ve Görüşme Listesini Siler */
  const deleteStudentPreInterviews = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");

    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteStudentPreInterviews(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    if (isSuccess) {
      setTimeout(() => {
        preInterviewsUIProps.setIds([]);
        dispatch(actions.getStudentPreInterviews(preInterviewsUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Ön Kayıt ve Görüşme Listesi{preInterviewsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{preInterviewsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportStudentPreInterviewsToExcel}
                exportToPDF={exportStudentPreInterviewsToPDF}
                print={printStudentPreInterviews}
                copy={copyStudentPreInterviews}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Ön Kayıt ve Görüşme"
                method={preInterviewsUIProps.openNewStudentPreInterviewPage}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteStudentPreInterviews(preInterviewsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <StudentPreInterviewsFilter />
        <StudentPreInterviewsTable
          deleteStudentPreInterviews={deleteStudentPreInterviews}
        />
      </CardBody>
    </Card>
  );
}